import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ICON } from '../../icons/iconIds';
import IconButton from '../icons/IconButton';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { documentDesignService } from '../../services/documentDesignService';

/**
 * Full-screen print preview. The API returns print HTML; Paged.js (loaded inside the iframe) paginates it
 * and posts progress + QA results back. "Save as PDF" uses the browser's print engine on the paginated pages.
 */
const DocumentPreview = ({ documentId, versionNo, title, sourceFileName, onClose }) => {
  const [quality, setQuality] = useState('draft');
  const [html, setHtml] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [pages, setPages] = useState(0);
  const [qa, setQa] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const frame = useRef(null);
  const startedAt = useRef(0);

  const load = useCallback(async () => {
    setPhase('loading');
    setHtml(null);
    setQa(null);
    setPages(0);
    setErrors([]);
    setLoadError(null);
    try {
      const text = await documentDesignService.getRenderHtml(documentId, { version: versionNo || undefined, quality });
      startedAt.current = Date.now();
      setHtml(text);
      setPhase('paginating');
    } catch (err) {
      setLoadError(err.message);
      setPhase('error');
    }
  }, [documentId, versionNo, quality]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onMessage = (e) => {
      if (!frame.current || e.source !== frame.current.contentWindow || e.data?.source !== 'dd-preview') return;
      if (e.data.type === 'progress') setPages(e.data.pages);
      if (e.data.type === 'error') setErrors((prev) => [...prev, e.data.message].slice(-10));
      if (e.data.type === 'done') {
        const result = { ...e.data.qa, seconds: Math.round((Date.now() - startedAt.current) / 100) / 10 };
        setQa(result);
        setPages(result.pages);
        setPhase('ready');
        documentDesignService
          .logRender(documentId, { version_no: versionNo || undefined, quality, page_count: result.pages, qa: result, renderer: 'pagedjs' })
          .catch((err) => console.warn('Render log failed:', err));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [documentId, versionNo, quality]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const printPdfTitle = () => {
    const raw = sourceFileName || title || 'Report';
    return raw.replace(/\.docx$/i, '').trim() || 'Report';
  };

  const print = () => {
    const win = frame.current?.contentWindow;
    if (!win) return;
    try {
      win.document.title = printPdfTitle();
    } catch {
      /* cross-origin guard; title is already set in render HTML */
    }
    win.print();
  };
  const missing = qa?.missing?.length || 0;
  const overflow = qa?.overflow?.length || 0;
  const missingImages = qa?.missing_images?.length || 0;
  const qaOk = qa && !missing && !qa.error;

  return (
    <div className="dd-preview-overlay" role="dialog" aria-modal="true" aria-label="Print preview">
      <div className="dd-preview-bar">
        <div className="dd-preview-title">
          <strong>{title}</strong>
          <span className="dd-muted">
            {phase === 'loading' && ' · Composing layout…'}
            {phase === 'paginating' && ` · Paginating… ${pages ? `${pages} pages` : ''}`}
            {phase === 'ready' && ` · ${pages} pages · ${qa.seconds}s`}
          </span>
        </div>
        <select value={quality} onChange={(e) => setQuality(e.target.value)} disabled={phase === 'loading' || phase === 'paginating'}>
          <option value="draft">Draft (fast, screen-resolution images)</option>
          <option value="print">Print (full-resolution images)</option>
        </select>
        <Button variant="secondary" size="sm" onClick={load} disabled={phase === 'loading' || phase === 'paginating'}>Refresh</Button>
        <Button size="sm" onClick={print} disabled={phase !== 'ready'}
          title={`Save as PDF (default name: ${printPdfTitle()}.pdf). Use Margins: None and enable Background graphics.`}>
          Save as PDF
        </Button>
        <IconButton icon={ICON.ddClose} onClick={onClose} ariaLabel="Close preview" />
      </div>

      {qa && (
        <div className={`dd-qa-bar ${qaOk ? 'ok' : 'fail'}`}>
          {qaOk ? (<><SelkirkIcon name={ICON.uiCheckOn} size={14} decorative /> QA passed: all {qa.expected} content blocks placed</>) : (<><SelkirkIcon name={ICON.ddQaBlocked} size={14} decorative /> QA: {missing} of {qa.expected} content blocks missing from the pages</>)}
          {overflow > 0 && (<><SelkirkIcon name={ICON.uiWarning} size={14} decorative /> {overflow} element(s) run past the page edge (pages {[...new Set(qa.overflow.map((o) => o.page))].slice(0, 8).join(', ')})</>)}
          {missingImages > 0 && (<><SelkirkIcon name={ICON.uiWarning} size={14} decorative /> {missingImages} image(s) could not be placed</>)}
          {qa.error && ` · ${qa.error}`}
          {qa.photos != null && ` · ${qa.photos} Brand Library photo${qa.photos === 1 ? '' : 's'} on cover/dividers`}
          {quality === 'draft' && qaOk && ' · Switch to Print quality before saving the final PDF.'}
        </div>
      )}
      {errors.length > 0 && <div className="dd-qa-bar fail">Renderer error: {errors[errors.length - 1]}</div>}

      <div className="dd-preview-stage">
        {phase === 'error' ? (
          <div className="dd-error-inline">{loadError}</div>
        ) : html ? (
          <iframe ref={frame} title="Report preview" srcDoc={html} className={phase === 'paginating' ? 'busy' : ''} />
        ) : null}
        {(phase === 'loading' || phase === 'paginating') && (
          <div className="dd-preview-progress">
            <Spinner size="large" />
            <p>{phase === 'loading' ? 'Applying review decisions and choosing photos…' : `Laying out pages… ${pages || ''}`}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentPreview;
